import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkIcon, UitkIconSize } from "uitk-react-icons";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkCard, UitkCardContentSection, UitkCardLink } from "uitk-react-cards";
import * as React from "react";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { observer } from "mobx-react";
import { FlightCardDefaultContentProps } from "components/flexComponents/Flights/typings";
import { UitkPriceLockup, UitkPriceLockupAlign } from "uitk-react-price-lockup";
import { locKeys } from "./l10";
import { getElapsedTime } from "components/flexComponents/Flights/util/getElapsedTime";
import { TripType } from "components/flexComponents/Flights/util/typings";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { UitkSpacing } from "uitk-react-spacing";
import { UitkText } from "uitk-react-text";

export const FlightCardDefaultContent: React.FC<FlightCardDefaultContentProps> = observer(
  ({ flight, index, isMobile, showCityName }) => {
    const { formatText } = useLocalization();
    const isOneWay = flight.tripType === TripType.ONE_WAY;
    const airCarrier = flight.airCarrier?.name || "Bargain Flight";
    const {
      airCarrierImageUrl,
      searchUrl,
      formattedDepartureDate,
      formattedReturnDate,
      formattedTicketPrice,
      elapsedHours,
    } = flight;
    const tripTypeText = isOneWay ? formatText(locKeys.flightOneWayText) : formatText(locKeys.flightRoundTripText);
    const originCode = flight.origin.code;
    const destinationCode = flight.destination.code;
    const originAirPort = showCityName ? flight.origin.cityName : flight.origin.name;
    const destinationAirPort = showCityName ? flight.destination.cityName : flight.destination.name;
    const elapsedDays = Math.floor(elapsedHours / 24);
    const formattedElapsedTime = getElapsedTime(elapsedHours, elapsedDays);
    const travelDate = isOneWay ? formattedDepartureDate : `${formattedDepartureDate} - ${formattedReturnDate}`;
    const arrowIconSize = isMobile ? UitkIconSize.SMALL : UitkIconSize.MEDIUM;
    const airlineLogoBlockSpace = isMobile ? "one" : "three";
    const airlineLogoShrink = isMobile ? 1 : 0;
    const airlineLogoRatio = isMobile ? UitkFigureAspectRatioType.R21_9 : UitkFigureAspectRatioType.R1_1;
    const arrowIcon = isOneWay ? (
      <UitkIcon name="arrow_forward" size={arrowIconSize} />
    ) : (
      <UitkIcon name="swap_horiz" size={arrowIconSize} />
    );
    const airLineIcon = (
      <UitkSpacing padding={{ inline: airlineLogoBlockSpace, block: airlineLogoBlockSpace }}>
        <UitkLayoutFlexItem maxWidth={100} minWidth={100} shrink={airlineLogoShrink}>
          <div>
            <UitkFigure className="all-image-fit-contain" ratio={airlineLogoRatio}>
              <LazyLoadedImage src={airCarrierImageUrl} alt={airCarrier} />
            </UitkFigure>
          </div>
        </UitkLayoutFlexItem>
      </UitkSpacing>
    );
    const dateAndLocations = (
      <div>
        <UitkText size={300} className="DateRange" weight="bold">
          {travelDate}
        </UitkText>
        <UitkSpacing margin={{ blockstart: "two" }}>
          <UitkLayoutGrid columns={4} space="two">
            <>
              <UitkLayoutGridItem>
                <div>
                  <UitkText size={500}>{originCode}</UitkText>
                  <UitkText size={100} overflow="truncate">
                    {originAirPort}
                  </UitkText>
                </div>
              </UitkLayoutGridItem>
              <UitkLayoutGridItem>{arrowIcon}</UitkLayoutGridItem>
              <UitkLayoutGridItem>
                <div>
                  <UitkText size={500}>{destinationCode}</UitkText>
                  <UitkText size={100} overflow="truncate">
                    {destinationAirPort}
                  </UitkText>
                </div>
              </UitkLayoutGridItem>
            </>
          </UitkLayoutGrid>
        </UitkSpacing>
      </div>
    );
    const priceDetails = (
      <UitkPriceLockup
        lockupPrice={formattedTicketPrice}
        priceSubtextFirst={tripTypeText}
        priceSubtextStandard={getElapsedTime(elapsedHours, elapsedDays)}
        a11yPrice={`${formattedTicketPrice} ${tripTypeText}, ${formattedElapsedTime}`}
        align={UitkPriceLockupAlign.RIGHT}
      />
    );

    return isMobile ? (
      <UitkCard border>
        <UitkLayoutFlex alignItems="center">
          <UitkLayoutFlexItem grow={1}>
            <UitkCardContentSection>
              <UitkLayoutGrid columns={6} space="two">
                <>
                  <UitkLayoutGridItem colSpan={2}>{airLineIcon}</UitkLayoutGridItem>
                  <UitkLayoutGridItem colSpan={4}>{priceDetails}</UitkLayoutGridItem>
                  <UitkLayoutGridItem colSpan={4}>{dateAndLocations}</UitkLayoutGridItem>
                </>
              </UitkLayoutGrid>
              <UitkCardLink>
                <TrackedLink
                  moduleName="Flights"
                  href={searchUrl}
                  rfrr={`flightDefaultCard-${index}`}
                  data-testid={`flightcard-link-${index}`}
                >
                  {airCarrier}
                </TrackedLink>
              </UitkCardLink>
            </UitkCardContentSection>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkCard>
    ) : (
      <UitkCard border>
        <UitkLayoutFlex alignItems="center">
          <>
            {airLineIcon}
            <UitkLayoutFlexItem grow={1}>
              <UitkCardContentSection>
                <UitkLayoutGrid columns={6} space="two">
                  <>
                    <UitkLayoutGridItem colSpan={4}>{dateAndLocations}</UitkLayoutGridItem>
                    <UitkLayoutGridItem colSpan={2}>{priceDetails}</UitkLayoutGridItem>
                  </>
                </UitkLayoutGrid>
                <UitkCardLink>
                  <TrackedLink moduleName="Flights" href={searchUrl} rfrr={`flightDefaultCard-${index}`}>
                    {airCarrier}
                  </TrackedLink>
                </UitkCardLink>
              </UitkCardContentSection>
            </UitkLayoutFlexItem>
          </>
        </UitkLayoutFlex>
      </UitkCard>
    );
  }
);
